import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  getAutopilotSlots,
  getAutopilotState,
  bookAutopilotAppointment
} from "../../services/api-declaration";
import { showError, showSuccess } from "../../services/toast-service";
import { CustomFontSizeDiv } from "./lead-agent-styles";

// A&M Autopilot (Asana 1219547818089406): the Lead Service finishes the
// Autopilot's job for a lead the Autopilot handed over. Questions, answers,
// budget and free slots come live from the Autopilot; the booking goes into
// the business's calendar through the Autopilot. Times are German time.
const BUSINESS_TIME_ZONE = "Europe/Berlin";
const dayFormatter = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: BUSINESS_TIME_ZONE
});
const timeFormatter = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: BUSINESS_TIME_ZONE
});
const formatDay = (iso) => dayFormatter.format(new Date(iso));
const formatTime = (iso) => timeFormatter.format(new Date(iso));

const ADDRESS_FIELDS = ["streetName", "buildingNumber", "postalCode", "city"];
const BOOKING_ERRORS = [
  "SLOT_TAKEN",
  "BUDGET_TOO_LOW",
  "ADDRESS_MISSING",
  "EMAIL_MISSING"
];

// a network error or a 5xx means the Autopilot is down; a 4xx carries the
// backend's own reason (e.g. lead not in the Autopilot)
export const autopilotErrorMessage = (error, t) => {
  const status = error?.response?.status;
  if (!status || status >= 500) return t("leadAgent.autopilot.unreachable");
  return error.response.data?.message || t("leadAgent.autopilot.unreachable");
};

// readOnly (AC 36): the Autopilot was switched off for the lead; the agent
// sees what the chat clarified, nothing goes back to the Autopilot
const AutopilotCard = ({
  lead,
  readOnly = false,
  callStarted,
  disabled,
  comment,
  searchVia,
  onAnswersChange,
  onBudgetChange,
  onBooked,
  onBookingChange
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // the message to show: "nicht erreichbar" or the backend's 4xx reason
  const [loadError, setLoadError] = useState("");
  const [answers, setAnswers] = useState({});
  const [budgetAmount, setBudgetAmount] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [address, setAddress] = useState({});
  const [email, setEmail] = useState("");
  const [isBooking, setIsBooking] = useState(false);
  // the panel locks its own buttons while a booking runs
  useEffect(() => {
    onBookingChange?.(isBooking);
  }, [isBooking, onBookingChange]);
  useEffect(() => () => onBookingChange?.(false), [onBookingChange]);

  const leadId = lead?._id;

  const loadState = useCallback(async () => {
    if (!leadId) return;
    setIsLoading(true);
    setLoadError("");
    try {
      const { data } = await getAutopilotState(leadId);
      const next = data?.data || null;
      setState(next);
      setAddress(next?.contact?.address || {});
      setEmail(next?.contact?.email || "");
    } catch (error) {
      const message = autopilotErrorMessage(error, t);
      setLoadError(message);
      // read-only: the inline note is enough, no toast on every lead
      if (!readOnly) showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [leadId, readOnly, t]);

  // a new lead: start from scratch
  useEffect(() => {
    setState(null);
    setAnswers({});
    setBudgetAmount("");
    setSlots(null);
    setSelectedSlot("");
    loadState();
    // only a new lead resets (not a language switch, loadState depends on t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const answerList = useMemo(
    () =>
      Object.entries(answers)
        .filter(([, value]) => value.answer?.trim() || value.unsure)
        .map(([key, value]) => ({
          key,
          answer: value.unsure ? "" : value.answer.trim(),
          unsure: Boolean(value.unsure)
        })),
    [answers]
  );

  // the panel sends them with "später kontaktieren" / "nicht geeignet" too
  useEffect(() => {
    onAnswersChange?.(answerList);
  }, [answerList, onAnswersChange]);
  useEffect(() => {
    onBudgetChange?.(budgetAmount);
  }, [budgetAmount, onBudgetChange]);

  const questions = state?.questions || [];
  const answered = questions.filter((q) => q.answered);
  const open = questions.filter((q) => !q.answered);
  const openDone = open.every(
    (q) => answers[q.key]?.unsure || answers[q.key]?.answer?.trim()
  );
  const budget = state?.budget;
  const budgetTooLow = budget?.verdict === "TOO_LOW" && !budgetAmount;
  const budgetMissing = budget?.required && !budget?.asked && !budgetAmount;
  // "In der Ausstellung" (am_autopilot #261): the lead comes to the business,
  // so no lead address is asked; the Autopilot sends the showroom's address
  const showroomAddress = state?.showroomAddress || null;
  const isOnSite = state?.appointmentType === "ONSITE" && !showroomAddress;
  const addressMissing =
    isOnSite && ADDRESS_FIELDS.some((field) => !address[field]?.trim());
  const canBook =
    callStarted &&
    !disabled &&
    !isBooking &&
    Boolean(selectedSlot) &&
    openDone &&
    !budgetMissing &&
    !budgetTooLow &&
    !addressMissing;

  const setAnswer = (key, patch) =>
    setAnswers((prev) => ({
      ...prev,
      [key]: { answer: "", unsure: false, ...prev[key], ...patch }
    }));

  const loadSlots = async () => {
    setSlotsLoading(true);
    try {
      const { data } = await getAutopilotSlots(leadId);
      setSlots(data?.data?.slots || []);
    } catch (error) {
      showError(autopilotErrorMessage(error, t));
    } finally {
      setSlotsLoading(false);
    }
  };

  const slotsByDay = useMemo(() => {
    const groups = new Map();
    (slots || []).forEach((slot) => {
      const day = formatDay(slot.startsAt);
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day).push(slot);
    });
    return [...groups.entries()];
  }, [slots]);

  const book = async () => {
    if (!canBook) return;
    setIsBooking(true);
    try {
      const { data } = await bookAutopilotAppointment(leadId, {
        startsAt: selectedSlot,
        answers: answerList,
        budgetAmount: budgetAmount || undefined,
        address: isOnSite ? address : undefined,
        email: email || undefined,
        comment,
        searchVia
      });
      const appointment = data?.data?.appointment;
      showSuccess(
        t(
          data?.data?.appointment?.wish
            ? "leadAgent.autopilot.wishNoted"
            : "leadAgent.autopilot.booked",
          {
            when: `${formatDay(appointment?.start || selectedSlot)}, ${formatTime(
              appointment?.start || selectedSlot
            )}`
          }
        )
      );
      onBooked?.();
    } catch (error) {
      const code = error.response?.data?.data?.code;
      if (code === "panel_action_running") {
        showError(t("leadAgent.autopilot.actionRunning"));
      } else if (BOOKING_ERRORS.includes(code)) {
        showError(t(`leadAgent.autopilot.bookingError.${code}`));
        if (code === "SLOT_TAKEN") {
          setSelectedSlot("");
          loadSlots();
        }
      } else {
        showError(autopilotErrorMessage(error, t));
      }
    } finally {
      setIsBooking(false);
    }
  };

  // what the Autopilot learned beyond the questions (am_autopilot, 03.11.2026):
  // the chat in 2–3 sentences, whether photos came, the earlier request
  const chatSummary = state?.chatSummary || null;
  const photos = state?.photos || null;
  const earlierAnswers = state?.earlierAnswers || [];

  const reasonKey = state?.cockpit?.reason || state?.handoverReason;
  // a reactivated old request also shows the Autopilot's outcome
  const reactivationOutcome =
    reasonKey === "reactivation" ? state?.cockpit?.reactivationOutcome : null;

  return (
    <Card className="mt-4 border-primary" data-testid="autopilot-card">
      <Card.Header className="d-flex justify-content-between align-items-center bg-white">
        <span className="fw-bold">{t("leadAgent.autopilot.title")}</span>
        {readOnly && (
          <Badge bg="secondary" data-testid="autopilot-ended">
            {t("leadAgent.autopilot.ended")}
          </Badge>
        )}
        {!readOnly && reasonKey && (
          <Badge bg="primary">
            {t(`leadAgent.autopilot.reason.${reasonKey}`, {
              defaultValue: reasonKey
            })}
            {reactivationOutcome &&
              ` · ${t(
                `leadAgent.autopilot.reactivationOutcome.${reactivationOutcome}`,
                { defaultValue: reactivationOutcome }
              )}`}
          </Badge>
        )}
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <div className="text-center">
            <Spinner size="sm" animation="border" />
          </div>
        )}
        {loadError && !isLoading && (
          <div className="text-center">
            <p className="text-danger mb-2">{loadError}</p>
            <Button size="sm" variant="outline-primary" onClick={loadState}>
              {t("leadAgent.autopilot.retry")}
            </Button>
          </div>
        )}
        {state && !isLoading && (
          <>
            {chatSummary && (
              <div className="mb-3" data-testid="autopilot-chat-summary">
                <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
                  {t("leadAgent.autopilot.chatSummary")}
                </CustomFontSizeDiv>
                <div className="mt-1">{chatSummary}</div>
              </div>
            )}
            {photos && (
              <div className="mb-3" data-testid="autopilot-photos">
                <span className="text-secondary">
                  {t("leadAgent.autopilot.photos")}:
                </span>{" "}
                <span className="fw-bold">
                  {photos.sent
                    ? t("leadAgent.autopilot.photosYes", {
                        count: photos.count
                      })
                    : t("leadAgent.autopilot.photosNo")}
                </span>
              </div>
            )}
            {answered.length > 0 && (
              <>
                <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
                  {t("leadAgent.autopilot.answered")}
                </CustomFontSizeDiv>
                <ul className="list-unstyled mb-3">
                  {answered.map((q) => (
                    <li key={q.key} className="mt-1">
                      <span className="text-secondary">{q.text}</span>{" "}
                      <span className="fw-bold">
                        {q.unsure ? t("leadAgent.autopilot.unsure") : q.answer}
                      </span>{" "}
                      {q.source && (
                        <Badge bg="light" text="dark">
                          {t(`leadAgent.autopilot.source.${q.source}`, {
                            defaultValue: q.source
                          })}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {readOnly && reasonKey && (
              <div className="small text-secondary mb-2">
                {t(`leadAgent.autopilot.reason.${reasonKey}`, {
                  defaultValue: reasonKey
                })}
                {reactivationOutcome &&
                  ` · ${t(
                    `leadAgent.autopilot.reactivationOutcome.${reactivationOutcome}`,
                    { defaultValue: reactivationOutcome }
                  )}`}
              </div>
            )}
            {readOnly && answered.length === 0 && open.length === 0 && (
              <p className="text-secondary mb-0">
                {t("leadAgent.autopilot.nothingClarified")}
              </p>
            )}
            {readOnly && open.length > 0 && (
              <>
                <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
                  {t("leadAgent.autopilot.open")}
                </CustomFontSizeDiv>
                <ul className="mb-3" data-testid="autopilot-open-list">
                  {open.map((q) => (
                    <li key={q.key}>{q.text}</li>
                  ))}
                </ul>
              </>
            )}
            {earlierAnswers.length > 0 && (
              <>
                <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
                  {t("leadAgent.autopilot.earlierAnswers")}
                </CustomFontSizeDiv>
                <ul
                  className="list-unstyled mb-3"
                  data-testid="autopilot-earlier-answers"
                >
                  {earlierAnswers.map((item, index) => (
                    <li key={`${item.question}-${index}`} className="mt-1">
                      <span className="text-secondary">{item.question}</span>{" "}
                      <span className="fw-bold">{item.answer}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {!readOnly && open.length > 0 && (
              <>
                <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
                  {t("leadAgent.autopilot.open")}
                </CustomFontSizeDiv>
                {open.map((q) => (
                  <Form.Group
                    className="mt-2"
                    key={q.key}
                    controlId={`autopilot-question-${q.key}`}
                  >
                    <Form.Label className="mb-1">{q.text}</Form.Label>
                    {q.options?.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mb-1">
                        {q.options.map((option) => (
                          <Button
                            key={option}
                            size="sm"
                            variant={
                              answers[q.key]?.answer === option &&
                              !answers[q.key]?.unsure
                                ? "primary"
                                : "outline-primary"
                            }
                            disabled={disabled}
                            onClick={() =>
                              setAnswer(q.key, {
                                answer: option,
                                unsure: false
                              })
                            }
                          >
                            {option}
                          </Button>
                        ))}
                      </div>
                    )}
                    <Form.Control
                      type="text"
                      placeholder={t("leadAgent.answer")}
                      value={answers[q.key]?.answer || ""}
                      disabled={disabled || answers[q.key]?.unsure}
                      onChange={({ target }) =>
                        setAnswer(q.key, {
                          answer: target.value,
                          unsure: false
                        })
                      }
                    />
                    <Form.Check
                      className="mt-1"
                      type="checkbox"
                      id={`autopilot-unsure-${q.key}`}
                      label={t("leadAgent.autopilot.unsure")}
                      checked={Boolean(answers[q.key]?.unsure)}
                      disabled={disabled}
                      onChange={({ target }) =>
                        setAnswer(q.key, { unsure: target.checked })
                      }
                    />
                  </Form.Group>
                ))}
              </>
            )}
            {readOnly && budget?.asked && (
              <div className="mt-2" data-testid="autopilot-budget-read">
                <span className="text-secondary">
                  {t("leadAgent.autopilot.budget")}
                  {budget.rangeText ? ` (${budget.rangeText})` : ""}:
                </span>{" "}
                <span className="fw-bold">
                  {budget.amount != null ? `${budget.amount} €` : "–"}
                </span>
              </div>
            )}
            {!readOnly && budget?.required && (
              <Form.Group className="mt-3" controlId="autopilot-budget">
                <Form.Label className="mb-1">
                  {t("leadAgent.autopilot.budget")}
                  {budget.rangeText ? ` (${budget.rangeText})` : ""}
                </Form.Label>
                {budget.asked && (
                  <div
                    className={budgetTooLow ? "text-danger fw-bold" : "fw-bold"}
                  >
                    {budget.amount != null ? `${budget.amount} €` : "–"}{" "}
                    {budgetTooLow &&
                      `– ${t("leadAgent.autopilot.budgetTooLow")}`}
                  </div>
                )}
                {/* the lead may name a different budget on the phone */}
                <Form.Control
                  className="mt-1"
                  type="number"
                  min="0"
                  placeholder="€"
                  value={budgetAmount}
                  disabled={disabled}
                  onChange={({ target }) => setBudgetAmount(target.value)}
                />
              </Form.Group>
            )}

            {/* no slots and no booking once the Autopilot is off */}
            {!readOnly && <hr />}
            {!readOnly && !slots && (
              <Button
                className="w-100"
                variant="success"
                disabled={!callStarted || disabled || slotsLoading}
                onClick={loadSlots}
              >
                {slotsLoading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  t("leadAgent.autopilot.book")
                )}
              </Button>
            )}
            {!readOnly && !callStarted && (
              <div className="text-secondary small mt-1">
                {t("leadAgent.autopilot.callFirst")}
              </div>
            )}
            {!readOnly && slots && (
              <>
                <CustomFontSizeDiv $fs="12" className="text-secondary fw-bold">
                  {t("leadAgent.autopilot.freeSlots")} ·{" "}
                  {t(
                    `leadAgent.autopilot.type.${
                      showroomAddress
                        ? "SHOWROOM"
                        : state.appointmentType || "PHONE"
                    }`
                  )}
                </CustomFontSizeDiv>
                {slots.length === 0 && (
                  <p className="text-danger mt-1">
                    {t("leadAgent.autopilot.noSlots")}
                  </p>
                )}
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {slotsByDay.map(([day, daySlots]) => (
                    <div key={day} className="mt-2">
                      <div className="small fw-bold">{day}</div>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {daySlots.map((slot) => (
                          <Button
                            key={slot.startsAt}
                            size="sm"
                            variant={
                              selectedSlot === slot.startsAt
                                ? "success"
                                : "outline-success"
                            }
                            disabled={disabled || isBooking}
                            onClick={() => setSelectedSlot(slot.startsAt)}
                          >
                            {formatTime(slot.startsAt)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {showroomAddress && (
                  <div className="mt-3 small" data-testid="autopilot-showroom">
                    {t("leadAgent.autopilot.showroom", {
                      address: showroomAddress
                    })}
                  </div>
                )}
                {isOnSite && (
                  <Row className="mt-3 g-2">
                    <Col xs={8}>
                      <Form.Control
                        aria-label={t("leadAgent.autopilot.address.streetName")}
                        placeholder={t(
                          "leadAgent.autopilot.address.streetName"
                        )}
                        value={address.streetName || ""}
                        onChange={({ target }) =>
                          setAddress((prev) => ({
                            ...prev,
                            streetName: target.value
                          }))
                        }
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Control
                        aria-label={t(
                          "leadAgent.autopilot.address.buildingNumber"
                        )}
                        placeholder={t(
                          "leadAgent.autopilot.address.buildingNumber"
                        )}
                        value={address.buildingNumber || ""}
                        onChange={({ target }) =>
                          setAddress((prev) => ({
                            ...prev,
                            buildingNumber: target.value
                          }))
                        }
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Control
                        aria-label={t("leadAgent.autopilot.address.postalCode")}
                        placeholder={t(
                          "leadAgent.autopilot.address.postalCode"
                        )}
                        value={address.postalCode || ""}
                        onChange={({ target }) =>
                          setAddress((prev) => ({
                            ...prev,
                            postalCode: target.value
                          }))
                        }
                      />
                    </Col>
                    <Col xs={8}>
                      <Form.Control
                        aria-label={t("leadAgent.autopilot.address.city")}
                        placeholder={t("leadAgent.autopilot.address.city")}
                        value={address.city || ""}
                        onChange={({ target }) =>
                          setAddress((prev) => ({
                            ...prev,
                            city: target.value
                          }))
                        }
                      />
                    </Col>
                  </Row>
                )}
                {!state.contact?.email && (
                  <Form.Control
                    className="mt-2"
                    type="email"
                    aria-label={t("leadAgent.autopilot.email")}
                    placeholder={t("leadAgent.autopilot.email")}
                    value={email}
                    onChange={({ target }) => setEmail(target.value)}
                  />
                )}
                {!openDone && (
                  <div className="text-secondary small mt-2">
                    {t("leadAgent.autopilot.answerOpenFirst")}
                  </div>
                )}
                <Button
                  className="w-100 mt-3"
                  variant="success"
                  disabled={!canBook}
                  onClick={book}
                >
                  {isBooking ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    t("leadAgent.autopilot.confirmBooking")
                  )}
                </Button>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default AutopilotCard;
